import { useCallback, useEffect, useState } from "react";
import { OutOfService } from "./out-of-service";
import { ScanStreamUnavailable } from "./scan-stream-unavailable";
import { UmannedEnter } from "./unmanned-enter";
import { UnmannedExit } from "./unmanned-exit";
import { ShiftService } from "../../api";
import { KioskLoading } from "../../components/kiosk-loading";
import { useServiceState } from "../../shared/hooks/useServiceState";
import { MaintenanceMode } from "./maintenance-mode";
import { useOperationCircuit } from "./use-operation-circuit";
import type {
  KioskCustomerView,
  KioskCameraDegradedContext,
  KioskPermissionDeniedContext,
  KioskSessionInvalidContext,
  KioskServiceFailureContext,
  KioskStreamFailureContext,
  MobileAppCheckoutPlacement,
  RefreshKioskSession,
} from "./types";

export type MobileAppCheckoutProps = {
  storeKey: string;
  zoneId: string;
  placement: MobileAppCheckoutPlacement;
  refreshSession?: RefreshKioskSession;
  onPermissionDenied: (context: KioskPermissionDeniedContext) => void;
  onSessionInvalid: (context: KioskSessionInvalidContext) => void;
  onServiceFailure?: (context: KioskServiceFailureContext) => void;
  onServiceRecovered?: () => void;
  onStreamFailure?: (context: KioskStreamFailureContext) => void;
  onStreamRecovered?: () => void;
  onCameraDegraded?: (context: KioskCameraDegradedContext) => void;
  onCameraRecovered?: () => void;
  onCustomerViewChange?: (view: KioskCustomerView) => void;
};

export function MobileAppCheckout({
  storeKey,
  zoneId,
  placement,
  refreshSession,
  onPermissionDenied,
  onSessionInvalid,
  onServiceFailure,
  onServiceRecovered,
  onStreamFailure,
  onStreamRecovered,
  onCameraDegraded,
  onCameraRecovered,
  onCustomerViewChange,
}: MobileAppCheckoutProps) {
  const [isScanStreamUnavailable, setIsScanStreamUnavailable] = useState(false);
  const operationCircuit = useOperationCircuit();
  const { status: serviceStatus, isMaintenanceMode } = useServiceState(
    ShiftService.FullAutonomousOnEnter,
    {
      // An exit kiosk faces customers who are already inside the store, so a
      // maintenance screen there does not turn anyone away, it locks them in.
      // Maintenance also means the backend is reachable and the turnstile still
      // works, which is exactly when blocking the exit is least defensible.
      trackMaintenance: placement !== "exit",
      onSessionInvalid: ({ operation, status }) =>
        onSessionInvalid({
          operation,
          reason: "refresh_rejected",
          status,
        }),
      onFailure: onServiceFailure,
      onRecovered: onServiceRecovered,
      onPermissionDenied: ({ operation }) =>
        onPermissionDenied({
          operation,
          status: 403,
        }),
    },
  );

  useEffect(() => {
    setIsScanStreamUnavailable(false);
  }, [placement, zoneId]);

  const handleStreamUnavailable = useCallback(
    (context: KioskStreamFailureContext) => {
      setIsScanStreamUnavailable(true);
      onStreamFailure?.(context);
    },
    [onStreamFailure],
  );

  const handleStreamRecovered = useCallback(() => {
    setIsScanStreamUnavailable(false);
    onStreamRecovered?.();
  }, [onStreamRecovered]);

  const customerView: KioskCustomerView =
    operationCircuit.isBlocking || serviceStatus === "unavailable"
      ? "out-of-service"
      : serviceStatus === "checking"
        ? "loading"
        : isMaintenanceMode
          ? "maintenance"
          : placement === "enter" && isScanStreamUnavailable
            ? "scan-unavailable"
            : "operating";

  useEffect(() => {
    onCustomerViewChange?.(customerView);
  }, [customerView, onCustomerViewChange]);

  if (customerView === "out-of-service") return <OutOfService />;

  if (customerView === "loading") return <KioskLoading />;

  if (customerView === "maintenance") return <MaintenanceMode />;

  return (
    <>
      {placement === "exit" ? (
        <UnmannedExit
          storeKey={storeKey}
          zoneId={zoneId}
          onPermissionDenied={onPermissionDenied}
          operationCircuit={operationCircuit}
          onCameraDegraded={onCameraDegraded}
          onCameraRecovered={onCameraRecovered}
        />
      ) : (
        <UmannedEnter
          storeKey={storeKey}
          zoneId={zoneId}
          refreshSession={refreshSession}
          onPermissionDenied={onPermissionDenied}
          onSessionInvalid={onSessionInvalid}
          operationCircuit={operationCircuit}
          onStreamUnavailable={handleStreamUnavailable}
          onStreamRecovered={handleStreamRecovered}
        />
      )}
      {customerView === "scan-unavailable" ? <ScanStreamUnavailable /> : null}
    </>
  );
}

export type {
  KioskCameraDegradedContext,
  KioskCustomerView,
  KioskPermissionDeniedContext,
  KioskSessionInvalidContext,
  KioskServiceFailureContext,
  KioskStreamFailureContext,
  MobileAppCheckoutPlacement,
  RefreshKioskSession,
} from "./types";
